/**
 * Bar series style overrides.
 *
 * Applies border radius (with stacked corner-radius logic), stacking,
 * bar width, per-category coloring, and horizontal mode.
 */
import type { TypeStyle } from '../../../../../edit/visualConfig';

interface BarContext {
  /** Index of this series in the full series array */
  seriesIdx: number;
  /** Index of the last bar-type series (receives corner radius when stacked) */
  lastBarIdx: number;
  /** Total count of bar-type series */
  barCount: number;
}

export function applyBarOverrides(series: any, cs: TypeStyle, ctx: BarContext): any {
  const patched = { ...series };
  const isStacked = cs.stacking === 'normal' || cs.stacking === 'percent';

  // Per-category coloring: each bar gets a different palette color
  // Only meaningful for single-series bars (multi-series uses series colors)
  if (cs.colorMode === 'category' && ctx.barCount === 1) {
    patched.colorBy = 'data';
  }

  // Border radius — only the top bar in a stack gets rounded corners.
  // For horizontal bars the "end" of the bar is on the right, so
  // radius goes on top-right + bottom-right instead of top-left + top-right.
  if (isStacked) {
    const isTop = ctx.seriesIdx === ctx.lastBarIdx;
    const r = isTop ? (cs.borderRadius ?? 0) : 0;
    const br = cs.horizontal ? [0, r, r, 0] : [r, r, 0, 0];
    patched.itemStyle = { ...patched.itemStyle, borderRadius: br };
  } else if (cs.borderRadius != null) {
    const r = cs.borderRadius;
    const br = cs.horizontal ? [0, r, r, 0] : [r, r, 0, 0];
    patched.itemStyle = { ...patched.itemStyle, borderRadius: br };
  }

  // Stacking
  if (isStacked) {
    patched.stack = 'total';
    patched.barWidth = patched.barWidth ?? '55%';
  }

  if (cs.barWidth != null) {
    patched.barWidth = `${cs.barWidth}%`;
  }

  return patched;
}